import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChartLine, Lightning, Spinner, TrendUp, TrendDown, Minus } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { BACKEND_URL } from '../config';

const MONTHS_MAP = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatMonth = (label) => {
  if (!label) return '';
  const [y, m] = label.split('-');
  return `${MONTHS_MAP[parseInt(m) - 1]} '${y.slice(2)}`;
};

const confidenceColor = (c) => c === 'high' ? 'text-green-500' : c === 'medium' ? 'text-yellow-500' : 'text-red-400';

const ClientForecast = ({ clientId, clientName }) => {
  const [months, setMonths] = useState('3');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const fetchForecast = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setAiAnalysis(null);
    try {
      const { data: result } = await axios.get(`${BACKEND_URL}/api/clients/${clientId}/forecast?months=${months}`, { withCredentials: true });
      setData(result);
    } catch (err) { console.error(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, months]);

  useEffect(() => { fetchForecast(); }, [fetchForecast]);

  const handleAiAnalysis = async () => {
    if (!data || aiLoading) return;
    setAiLoading(true);
    try {
      const { data: result } = await axios.post(`${BACKEND_URL}/api/clients/${clientId}/forecast/ai`, {
        forecast: data.forecast,
        historical: data.historical,
        trend: data.trend,
        model_accuracy: data.model_accuracy,
      }, { withCredentials: true });
      setAiAnalysis(result.analysis);
    } catch (err) { console.error(err.message);
      setAiAnalysis('Failed to generate AI analysis. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground text-sm">Loading forecast...</div>;
  if (!data) return null;
  if (data.message) return <div className="text-center py-6 text-muted-foreground text-sm">{data.message}</div>;

  // Merge historical + forecast into one chart dataset
  const chartData = [
    ...data.historical.map(h => ({ month: h.month, revenue: h.revenue, type: 'historical' })),
    // Bridge: duplicate last historical point as first forecast point
    ...(data.historical.length > 0 ? [{
      month: data.historical[data.historical.length - 1].month,
      predicted: data.historical[data.historical.length - 1].revenue,
      type: 'bridge',
    }] : []),
    ...data.forecast.map(f => ({ month: f.month, predicted: f.predicted_revenue, confidence: f.confidence, type: 'forecast' })),
  ];

  const trendIcon = data.trend === 'growing'
    ? <TrendUp size={18} weight="bold" className="text-green-500" />
    : data.trend === 'declining'
    ? <TrendDown size={18} weight="bold" className="text-destructive" />
    : <Minus size={18} weight="bold" className="text-muted-foreground" />;

  const trendLabel = data.trend === 'growing' ? 'Growing' : data.trend === 'declining' ? 'Declining' : 'Stable';

  return (
    <div className="bg-card border border-border rounded-sm p-4 sm:p-5 space-y-4" data-testid="client-forecast-widget">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ChartLine size={18} weight="regular" className="text-primary" />
          <span className="font-semibold text-sm">Revenue Forecast</span>
          {clientName && <span className="text-xs text-muted-foreground hidden sm:inline">— {clientName}</span>}
        </div>
        <div className="flex items-center gap-2">
          <Select value={months} onValueChange={(v) => setMonths(v)}>
            <SelectTrigger className="w-[130px] rounded-sm h-8 text-xs" data-testid="forecast-months-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 Months</SelectItem>
              <SelectItem value="6">6 Months</SelectItem>
              <SelectItem value="12">12 Months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <div className="bg-muted/30 rounded-sm p-3">
          <div className="text-[10px] sm:text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1">Trend</div>
          <div className="flex items-center gap-1.5">
            {trendIcon}
            <span className="text-sm font-semibold">{trendLabel}</span>
          </div>
        </div>
        <div className="bg-muted/30 rounded-sm p-3">
          <div className="text-[10px] sm:text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1">Accuracy</div>
          <div className="text-sm font-semibold">{data.model_accuracy}%</div>
        </div>
        <div className="bg-muted/30 rounded-sm p-3">
          <div className="text-[10px] sm:text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1">Avg Historical</div>
          <div className="text-sm font-semibold">₹{data.avg_historical?.toLocaleString()}</div>
        </div>
        <div className="bg-muted/30 rounded-sm p-3">
          <div className="text-[10px] sm:text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1">Avg Forecast</div>
          <div className="text-sm font-semibold">₹{data.avg_forecast?.toLocaleString()}</div>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="month" tickFormatter={formatMonth} stroke="hsl(var(--foreground))" style={{ fontSize: '10px' }} />
          <YAxis stroke="hsl(var(--foreground))" style={{ fontSize: '10px' }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
          <Tooltip
            formatter={(v, name) => [`₹${v?.toLocaleString()}`, name === 'revenue' ? 'Actual' : 'Predicted']}
            labelFormatter={formatMonth}
            contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.25rem', fontSize: '12px' }}
          />
          {data.historical.length > 0 && (
            <ReferenceLine
              x={data.historical[data.historical.length - 1].month}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="4 4"
              strokeWidth={1}
            />
          )}
          <Line type="monotone" dataKey="revenue" stroke="hsl(var(--accent))" strokeWidth={2.5} dot={{ fill: 'hsl(var(--accent))', r: 3 }} connectNulls={false} />
          <Line type="monotone" dataKey="predicted" stroke="hsl(var(--primary))" strokeWidth={2} strokeDasharray="6 3" dot={{ fill: 'hsl(var(--primary))', r: 3 }} connectNulls={false} />
        </LineChart>
      </ResponsiveContainer>

      {/* Forecast table */}
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="w-full min-w-[400px] text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Month</th>
              <th className="text-right py-2 px-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Predicted Revenue</th>
              <th className="text-center py-2 px-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {data.forecast.map((f) => (
              <tr key={f.month} className="border-b border-border" data-testid={`forecast-row-${f.month}`}>
                <td className="py-2 px-3">{formatMonth(f.month)}</td>
                <td className="py-2 px-3 text-right font-semibold">₹{f.predicted_revenue.toLocaleString()}</td>
                <td className={`py-2 px-3 text-center text-xs font-bold uppercase tracking-wide ${confidenceColor(f.confidence)}`}>{f.confidence}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AI Analysis */}
      <div className="border-t border-border pt-3">
        {!aiAnalysis ? (
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm w-full sm:w-auto"
            onClick={handleAiAnalysis}
            disabled={aiLoading}
            data-testid="ai-forecast-button"
          >
            {aiLoading ? <Spinner size={16} className="animate-spin mr-2" /> : <Lightning size={16} weight="fill" className="mr-2 text-yellow-500" />}
            {aiLoading ? 'Analyzing...' : 'AI Analysis (GPT-5.2)'}
          </Button>
        ) : (
          <div className="space-y-2" data-testid="ai-forecast-result">
            <div className="flex items-center gap-2 mb-1">
              <Lightning size={14} weight="fill" className="text-yellow-500" />
              <span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">AI Commentary</span>
            </div>
            <p className="text-sm leading-relaxed text-foreground/90">{aiAnalysis}</p>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setAiAnalysis(null); }} data-testid="ai-forecast-retry">Regenerate</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientForecast;
